<template>
  <div>
    <PageScrolling
      v-if="!isUpdateAndroidApp"
      hasWideBreakpoint
      :sections="pageScrollingSections"
      shouldDetectScrolling
      :visibilityThreshold="0.2"
    >
      <template
        id="applicationDetails"
        v-slot:#applicationDetails
      >
        <Card>
          <HeadingSmall>
            Application Details
          </HeadingSmall>
          <div v-if="isPublicApp || isPrivateApp">
            <Grid>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Type:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ formatType }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Version:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ item.settings[0].googleAndroid.appVersion }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Requires Android:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                6.0 or higher
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Updated on:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ formatDateTime }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Offered by:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ item.settings[0].googleAndroid.author }}
              </GridItem>
            </Grid>
          </div>
          <div v-else>
            <Grid>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Type:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ formatType }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Version:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ item.settings[0].googleAndroid.appVersion }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                App URL:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ item.settings[0].googleAndroid.startUrl }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Display Mode:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ item.settings[0].googleAndroid.displayMode }}
              </GridItem>
              <GridItem
                :class="$style.boldText"
                :gridSpan="6"
              >
                Updated on:
              </GridItem>
              <GridItem
                :class="$style.rightAlign"
                :gridSpan="6"
              >
                {{ formatDateTime }}
              </GridItem>
            </Grid>
          </div>
          <div v-if="isPrivateApp || isPublicApp">
            <HeadingSmall :class="$style.marginTop">
              Description
            </HeadingSmall>
            <ParagraphBody>
              <pre
                :class="$style.descriptionTextFormat"
              >
              {{ formatHtmlText }}
              </pre>
            </ParagraphBody>
            <div
              v-if="item.settings[0].googleAndroid.managedProperties"
              :class="$style.iconButtonPosition"
            >
              <IconButton
                icon="externalLink"
                iconPosition="right"
                text="Configuration"
                type="primary"
                @click="handleConfigureButton"
              />
            </div>
          </div>
        </Card>
      </template>
      <template
        id="applicationInstallMode"
        v-slot:#applicationInstallMode
      >
        <Card v-if="isPublicApp || isPrivateApp">
          <HeadingSmall>
            Application Install Mode
            <TooltipIcon
              icon="info"
              text=""
            />
          </HeadingSmall>
          <ChoiceChipGroup
            name="installChoiceChips"
            :options="$options.applicationInstallMode"
            :value="item.settings[0].googleAndroid.installType"
            @input="(newValue) => handleApplicationInstallMode(newValue)"
          />
        </Card>
      </template>
      <template
        id="applicationUpdateMode"
        v-slot:#applicationUpdateMode
      >
        <Card v-if="isPublicApp || isPrivateApp">
          <HeadingSmall>
            Application Update Mode
            <TooltipIcon
              icon="info"
              text=""
            />
          </HeadingSmall>
          <ChoiceChipGroup
            name="updateChoiceChips"
            :options="$options.applicationUpdateMode"
            :value="item.settings[0].googleAndroid.autoUpdateMode"
            @input="(newValue) => handleApplicationUpdateMode(newValue)"
          />
        </Card>
      </template>
      <template
        id="runtimePermissions"
        v-slot:#runtimePermissions
      >
        <Card v-if="isPublicApp || isPrivateApp">
          <HeadingSmall>
            Runtime Permissions
            <TooltipIcon
              icon="info"
              text=""
            />
          </HeadingSmall>
          <ChoiceChipGroup
            name="permissionChoiceChips"
            :options="$options.runtimePermissions"
            :value="item.settings[0].googleAndroid.runtimePermission"
            @input="(newValue) => handleRuntimePermissions(newValue)"
          />
          <CollapsibleSection
            v-if="item.settings[0].googleAndroid.permissionGrants.length"
            buttonText="Expand to customize individual permissions"
            :class="$style.cllapsibleItem"
            :isExpanded="isCustomizePermissionExpanded"
            @expand="handleCustomizePermissionExpanded"
          >
            <template v-slot:content>
              <div
                v-for="(value, index) in item.settings[0].googleAndroid.permissionGrants"
                :key="index"
              >
                <RadioGroup
                  :class="$style.radioItem"
                  :label="getPermissionLabel(value.id)"
                  :name="value.id"
                  :options="$options.radioOption"
                  :showValidationMessage="false"
                  :value="value.policy"
                  @input="(newValue) => handleRadioButton(index, value.id, newValue)"
                />
              </div>
            </template>
          </CollapsibleSection>
          <template v-slot:footer>
            <ButtonGroupSave
              :class="$style.buttonGroupSaveSpacing"
              :isFullWidth="false"
              @primaryAction="handleSave"
              @secondaryAction="handleCancel"
            />
          </template>
        </Card>
      </template>
    </PageScrolling>
    <AddNewGoogleApp
      v-else
      :class="$style.fullHeight"
      :isNewAndroidApp="!isUpdateAndroidApp"
      @configureComplete="handleConfigureComplete"
    />
  </div>
</template>

<script>
import {
  AndroidEmmConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  ButtonGroupSave,
  Card,
  ChoiceChipGroup,
  CollapsibleSection,
  Grid,
  GridItem,
  HeadingSmall,
  IconButton,
  PageScrolling,
  ParagraphBody,
  RadioGroup,
  TooltipIcon,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AddNewGoogleApp from 'client/SoftwareApps/AddNewGoogleApp.vue';
import Util from 'client/util/Util';

const moment = require('moment');

const { TabItem } = Types;
const { googleAppsDetailsText } = AndroidEmmConsts;
const softwareAppsModel = StoreModelMap[ItemTypeConsts.softwareApps];
const googleModule = StoreModuleMap[ItemTypeConsts.mdmGoogle];
const applicationInstallMode = [
  {
    name: googleAppsDetailsText.applicationInstallMode.availableTitle,
    value: googleAppsDetailsText.applicationInstallMode.availableValue,
  },
  {
    name: googleAppsDetailsText.applicationInstallMode.installTitle,
    value: googleAppsDetailsText.applicationInstallMode.installValue,
  },
  {
    name: googleAppsDetailsText.applicationInstallMode.blockTitle,
    value: googleAppsDetailsText.applicationInstallMode.blockValue,
  },
];
const applicationUpdateMode = [
  {
    name: googleAppsDetailsText.applicationUpdateMode.defaultTitle,
    value: googleAppsDetailsText.applicationUpdateMode.defaultValue,
  },
  {
    name: googleAppsDetailsText.applicationUpdateMode.priorityTitle,
    value: googleAppsDetailsText.applicationUpdateMode.priorityValue,
  },
  {
    name: googleAppsDetailsText.applicationUpdateMode.postponeTitle,
    value: googleAppsDetailsText.applicationUpdateMode.postponeValue,
  },
];
const runtimePermissions = [
  {
    name: googleAppsDetailsText.runtimePermissions.allowTittle,
    value: googleAppsDetailsText.runtimePermissions.allowValue,
  },
  {
    name: googleAppsDetailsText.runtimePermissions.promptTitle,
    value: googleAppsDetailsText.runtimePermissions.promptValue,
  },
  {
    name: googleAppsDetailsText.runtimePermissions.denyTitle,
    value: googleAppsDetailsText.runtimePermissions.denyValue,
  },
];
const radioOption = [
  { value: 'GRANT', label: 'Allow' },
  { value: 'PROMPT', label: 'Prompt' },
  { value: 'DENY', label: 'Deny' },
];

export default {
  name: 'GoogleAppsDetailsPanel',

  components: {
    AddNewGoogleApp,
    ButtonGroupSave,
    Card,
    ChoiceChipGroup,
    CollapsibleSection,
    Grid,
    GridItem,
    HeadingSmall,
    IconButton,
    PageScrolling,
    ParagraphBody,
    RadioGroup,
    TooltipIcon,
  },

  applicationInstallMode,
  applicationUpdateMode,
  radioOption,
  runtimePermissions,

  data: () => ({
    isCustomizePermissionExpanded: false,
    isPrivateApp: false,
    isPublicApp: false,
    isUpdateAndroidApp: false,
    isWebApp: false,
    permissionGrants: [],
    route: `/${ItemTypeRoutes[ItemTypeConsts.softwareApps]}`,
  }),

  computed: {
    ...mapGetters(softwareAppsModel, [
      'item',
    ]),

    formatDateTime() {
      return moment(this.item.settings[0].googleAndroid.updateTime).format('MMMM d, YYYY, h:mm A');
    },

    formatHtmlText() {
      const htmlRegexG = /<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g;
      const str = this.item.settings[0].googleAndroid.fullDescription
        .replace(/  +/g, '')
        .replace(/<br\s*\/?>/g, '\n')
        .replace(htmlRegexG, '');
      return str;
    },

    formatType() {
      let formattedValue = this.item.settings[0].googleAndroid.type;
      if (formattedValue === 'WEBAPP') {
        formattedValue = 'WEB';
      }
      const updatedValue = formattedValue.toLowerCase();
      return updatedValue.charAt(0).toUpperCase() + updatedValue.slice(1);
    },

    pageScrollingSections() {
      const pageScrollingSectionsList = [
        new TabItem({
          disabled: false,
          label: 'Application Details',
          key: '#applicationDetails',
        }),
      ];
      if (this.isPublicApp) {
        pageScrollingSectionsList.push(
          new TabItem({
            disabled: false,
            label: 'Application Install Mode',
            key: '#applicationInstallMode',
          }),
        );
        pageScrollingSectionsList.push(
          new TabItem({
            disabled: false,
            label: 'Application Update Mode',
            key: '#applicationUpdateMode',
          }),
        );
        pageScrollingSectionsList.push(
          new TabItem({
            disabled: false,
            label: 'Runtime Permissions',
            key: '#runtimePermissions',
          }),
        );
      }
      if (this.isPrivateApp) {
        pageScrollingSectionsList.push(
          new TabItem({
            disabled: false,
            label: 'Runtime Permissions',
            key: '#runtimePermissions',
          }),
        );
      }

      return pageScrollingSectionsList;
    },
  },

  mounted() {
    this.permissionGrants = this.item.settings[0].googleAndroid.permissionGrants;
    this.findAppType();
  },

  methods: {
    ...mapActions(softwareAppsModel, [
      'saveItem',
      'updateItem',
      'setItemGoogleAndroid',
      'updatePolicy',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    ...mapActions(googleModule, [
      'updateAndroidAppState',
      'updateNewAndroidAppState',
      'updatePackageName',
      'setItemStateToCreate',
    ]),

    findAppType() {
      if (this.item.settings[0].googleAndroid.type === 'PRIVATE') {
        this.isPrivateApp = true;
      } else if (this.item.settings[0].googleAndroid.type === 'PUBLIC') {
        this.isPublicApp = true;
      } else {
        this.isWebApp = true;
      }
    },

    getPermissionLabel(value) {
      const permissionStrList = value.split('.');
      return permissionStrList[permissionStrList.length - 1];
    },

    handleApplicationInstallMode(newValue) {
      this.setItemGoogleAndroid({ key: 'installType', value: newValue });
    },

    handleApplicationUpdateMode(newValue) {
      this.setItemGoogleAndroid({ key: 'autoUpdateMode', value: newValue });
    },

    handleCancel() {
      Util.redirect(`${this.route}/google`);
    },

    handleConfigureButton() {
      this.updatePackageName(this.item.settings[0].packageId);
      this.isUpdateAndroidApp = true;
    },

    handleConfigureComplete() {
      this.isUpdateAndroidApp = false;
    },

    handleCustomizePermissionExpanded() {
      this.isCustomizePermissionExpanded = !this.isCustomizePermissionExpanded;
    },

    handleRadioButton(index, id, policy) {
      this.permissionGrants[index] = {
        id,
        policy,
      };
    },

    handleRuntimePermissions(newValue) {
      this.setItemGoogleAndroid({ key: 'runtimePermission', value: newValue });
    },

    handleSave() {
      this.setItemGoogleAndroid({ key: 'permissionGrants', value: this.permissionGrants });
      this.setItemStateToCreate(false);
      this.saveItem()
        .then(() => {
          this.triggerSuccessNotification({
            title: 'Success',
            message: 'application saved successfully',
          });
        })
        .catch((err) => {
          this.triggerErrorNotification({
            title: 'Error',
            message: err.message,
          });
        })
        .finally(() => {
          Util.redirect(`${this.route}/google`);
        });
    },
  },
};
</script>

<style module>
.cllapsibleItem {
  margin-top: var(--jc-spacer-large);
}

.radioItem {
  border-bottom: var(--jc-border);
  margin-top: var(--jc-spacer);
}

.radioItem ul li {
  margin-bottom: var(--jc-spacer-small) !important;
}

.buttonGroupSaveSpacing {
  padding-top: var(--jc-spacer-medium);
}

.iconButtonPosition {
  padding: 0;
  text-align: right;
  width: 100%;
}

.rightAlign {
  text-align: right;
}

.fullHeight {
  height: 100%;
  padding-bottom: 90px !important;
  position: absolute;
  width: 100%;
}

.marginTop {
  margin-top: var(--jc-spacer-large) !important;
}

.boldText {
  font-weight: 600;
}

.descriptionTextFormat {
  font-family: var(--jc-font);
  font-size: var(--jc-size-0p875);
  font-weight: 600;
  white-space: pre-wrap;
  white-space: -moz-pre-wrap;
  white-space: -o-pre-wrap;
  word-wrap: break-word;
}
</style>
